<template>
    <div ref="paymentElementContainer">
        <!--Stripe.js injects the Payment Element-->
    </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue'
import { graphql } from '~/resources/graphql'
import {
    loadStripe,
    type Stripe,
    type StripeElements,
    type StripeError,
    type StripePaymentElement
} from '@stripe/stripe-js'
import { useRpQuery } from '~/composables/graphql'
import type { TokenPaymentMethod } from '~/components/Checkout/Sections/Payment/payment-method'
import { useCheckoutFormProcessing } from '~/components/Checkout/Forms/use-checkout-form'

const props = defineProps<{
    paymentSourceId: string
    checkoutId?: string
    amount: string
}>()

const emit = defineEmits<{
    (e: 'paymentMethod', payload: TokenPaymentMethod | undefined): void
    (e: 'displayStripeError', payload: StripeError): void
}>()

type StripePaymentIntentData = {
    stripePaymentIntentClientSecret: string
    stripeAccountId: string
    stripeReturnUrl: string
    stripePublishKey: string
    stripeCurrency: string
}

let stripe: Stripe | null = null
let elements: StripeElements | null = null
let paymentIntent: StripePaymentIntentData | null = null
let paymentElement: StripePaymentElement | null = null
let paymentElementReady = false
let disposed = false
let initializationProcessing = false
const paymentElementContainer = ref<HTMLElement>()

const { incrementProcessing, decrementProcessing } = useCheckoutFormProcessing()

function releaseInitializationProcessing(): void {
    if (initializationProcessing) {
        // Stripe can fail and unmount concurrently, so release this form's lock only once.
        initializationProcessing = false
        decrementProcessing()
    }
}

function displayInitializationError(error: unknown): void {
    // Initialization failures occur before Stripe can return a confirmation error.
    emit('displayStripeError', {
        type: 'api_error',
        message: error instanceof Error ? error.message : undefined
    })
}

// Each keyed mount owns exactly one intent for its source, checkout, and amount.
const query = useRpQuery()
async function initializeStripePaymentForm(): Promise<void> {
    const checkoutId = props.checkoutId
    if (checkoutId === undefined) {
        // A malformed mount must not leave the checkout controls disabled.
        releaseInitializationProcessing()
        displayInitializationError(
            new Error('Checkout ID is required for a Stripe payment intent')
        )
        return
    }

    try {
        const response = await query(
            graphql(/* GraphQL */ `
                mutation CreateStripePaymentIntent(
                    $input: CreateStripePaymentIntentInput!
                ) {
                    createStripePaymentIntent(input: $input) {
                        stripePaymentIntentClientSecret
                        stripeAccountId
                        stripeReturnUrl
                        stripePublishKey
                        stripeCurrency
                    }
                }
            `),
            {
                input: {
                    paymentSourceId: props.paymentSourceId,
                    checkoutId,
                    amount: props.amount
                }
            }
        )
        if (disposed) {
            return
        }

        const intentData = response.createStripePaymentIntent
        const nextStripe = await loadStripe(intentData.stripePublishKey, {
            stripeAccount: intentData.stripeAccountId
        })
        if (disposed) {
            return
        }

        if (nextStripe === null) {
            throw new Error('Stripe object not initialized')
        }

        const nextElements = nextStripe.elements({
            clientSecret: intentData.stripePaymentIntentClientSecret
        })
        const nextPaymentElement = nextElements.create('payment', {})
        if (paymentElementContainer.value === undefined) {
            nextPaymentElement.destroy()
            throw new Error('Stripe payment element container not initialized')
        }

        nextPaymentElement.once('ready', () => {
            if (!disposed && paymentElement === nextPaymentElement) {
                paymentElementReady = true
                // Elements is now safe to confirm, so allow the checkout form to submit.
                releaseInitializationProcessing()
            }
        })
        nextPaymentElement.once('loaderror', ({ error }) => {
            if (!disposed && paymentElement === nextPaymentElement) {
                paymentElementReady = false
                nextPaymentElement.destroy()
                // Prevent a queued ready event from re-enabling this failed Element.
                paymentElement = null
                // Initialization cannot recover without a refresh, so restore the checkout controls.
                releaseInitializationProcessing()
                emit('displayStripeError', error)
            }
        })

        stripe = nextStripe
        elements = nextElements
        paymentIntent = intentData
        paymentElement = nextPaymentElement
        nextPaymentElement.mount(paymentElementContainer.value)
    } catch (error) {
        if (!disposed) {
            // A partially mounted Element must remain non-submittable after a mount failure.
            paymentElementReady = false
            paymentElement?.destroy()
            paymentElement = null
            releaseInitializationProcessing()
            displayInitializationError(error)
        }
    }
}

onMounted(() => {
    // Hold the shared checkout lock before the provider request begins.
    initializationProcessing = true
    incrementProcessing()
    void initializeStripePaymentForm()
})

onBeforeUnmount(() => {
    // A remount for another amount or source must not let this request mount afterward.
    disposed = true
    releaseInitializationProcessing()
    paymentElement?.destroy()
})

//--------------------------------------------
// submit stripe form - called from parent
// to capture payment
//--------------------------------------------
async function submitStripeElementsForm(): Promise<boolean> {
    if (
        stripe === null ||
        elements === null ||
        paymentIntent === null ||
        !paymentElementReady
    ) {
        // Callers must not treat an unready Element as a submitted payment.
        return false
    }

    const result = await stripe.confirmPayment({
        elements,
        confirmParams: {
            expand: ['payment_method'],
            return_url: paymentIntent.stripeReturnUrl
        },
        redirect: 'if_required'
    })

    // Ignore a confirmation result from an Element that has since been replaced.
    if (disposed) {
        return false
    }

    if (result.paymentIntent) {
        if (result.paymentIntent.payment_method === null) {
            throw new Error('Payment method null')
        }

        if (typeof result.paymentIntent.payment_method === 'string') {
            throw new Error('Payment method is not expanded')
        }

        //
        // post token and success message to parent
        //

        const paymentMethod: TokenPaymentMethod = {
            type: 'TokenPaymentMethod',
            token: 'STRIPE_PI|' + result.paymentIntent.id,
            isCard: false,
            paymentTodayAllowed: false,
            brand: result.paymentIntent.payment_method.type,
            lastFour: null,
            accountExpirationDate: null
        }

        // We have a card - so add some details
        if (result.paymentIntent.payment_method.card !== undefined) {
            paymentMethod.isCard = true
            paymentMethod.brand =
                result.paymentIntent.payment_method.card.brand.toLocaleUpperCase()
            paymentMethod.lastFour =
                result.paymentIntent.payment_method.card.last4
            paymentMethod.accountExpirationDate =
                result.paymentIntent.payment_method.card.exp_year +
                '-' +
                result.paymentIntent.payment_method.card.exp_month +
                '-01'
        }

        emit('paymentMethod', paymentMethod)
        return true
    }

    if (result.error) {
        emit('displayStripeError', result.error)
        // Confirmation ran, even though Stripe rejected the entered payment details.
        return true
    }

    // Stripe's result union should always provide either an intent or an error.
    throw new Error('Stripe did not return a payment result')
}

defineExpose({
    submitStripeElementsForm
})
</script>
